import React, { Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import ProtectedRoutes from './common/ProtectedRoutes'
import PublicRoutes from './common/PublicRoutes'
import '@fontsource/source-sans-pro' // Defaults to weight 400.
import Maintenance from './views/pages/Maintenance'
import { useSelector, useDispatch } from 'react-redux'
import socketService from './redux/socket/service/service'
import { initSocketListeners } from './redux/socket/sockets'
import { useTranslation } from 'react-i18next'
import { Get } from './redux/api'
import branding from './utils/branding'
import './index.css'
import './scss/style.scss'

// Loading component with minimal imports
const LoadingSpinner = () => (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
)

// Lazy load all components
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))
const Login = React.lazy(() => import('./views/pages/login/Login'))
const CompanySelection = React.lazy(() =>
  import('./views/pages/companySelection/CompanySelection'),
)
const Register = React.lazy(() => import('./views/pages/register/Register'))
const Resetpasswod = React.lazy(() =>
  import('./views/pages/Resetpassword/Resetpasswod'),
)
const FormADR = React.lazy(() => import('./views/triage/formADR'))
const ResetpasswodLinlk = React.lazy(() =>
  import('./views/pages/Resetpassword/redrictLoginpage'),
)
const LoginOTP = React.lazy(() => import('./views/pages/login/otpLogin'))
const ForgetPassword = React.lazy(() =>
  import('./views/pages/ForgetPassword/forgetPassword'),
)
const ChangePassword = React.lazy(() =>
  import('./views/pages/ChangePassword/changePassword'),
)
const FormADRRF = React.lazy(() => import('./views/triage/formADRRF'))

const TGA = React.lazy(() => import('./views/TGA/adverseEventReport'))

const App = () => {
  const { user } = useSelector((state) => state.authReducer)
  const { caseInformation } = useSelector((state) => state.basicCaseReducer)
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const isMaintenance = process.env.REACT_APP_MAINTENANCE

  // Set document title and favicon based on branding
  useEffect(() => {
    document.title = branding.productName
    const favicon = document.getElementById('favicon')
    if (favicon) {
      favicon.href = `${process.env.PUBLIC_URL || ''}/${branding.favicon}`
    }
  }, [])

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue =
        'Are you sure you want to leave? Your changes may not be saved.'
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  // Load user language preference on login
  useEffect(() => {
    const loadUserLanguage = async () => {
      if (user?.userId) {
        // Store current userId in localStorage for user-specific language
        localStorage.setItem('currentUserId', user.userId.toString())

        // First, check localStorage for immediate language setting (before API call)
        const userLangKey = `i18nextLng_${user.userId}`
        const storedLang = localStorage.getItem(userLangKey)
        if (storedLang && (storedLang === 'en' || storedLang === 'ar')) {
          // Set language immediately from localStorage
          await i18n.changeLanguage(storedLang, { userId: user.userId })
        }

        // Then, try to get from backend to sync
        try {
          const config = process.env
          const res = await Get(
            `${config.REACT_APP_END_POINT}/users/language/${user.userId}`,
            user,
          )
          if (res.success && res.data?.language) {
            // Only change if different from current language
            if (i18n.language !== res.data.language) {
              await i18n.changeLanguage(res.data.language, {
                userId: user.userId,
              })
            }
            // Update user-specific localStorage
            localStorage.setItem(userLangKey, res.data.language)
          }
        } catch (error) {
          console.error('Error loading user language from backend:', error)
          // If no stored language and API fails, keep current language or use default
          if (!storedLang) {
            await i18n.changeLanguage('en', { userId: user.userId })
          }
        }
      } else {
        // User logged out - clear currentUserId but keep language preference
        localStorage.removeItem('currentUserId')
      }
    }
    loadUserLanguage()
  }, [user?.userId, i18n])

  useEffect(() => {
    const token = user?.token
    if (!token) return

    socketService.connect(token)
    initSocketListeners(dispatch, user, caseInformation)

    return () => {
      socketService.disconnect()
    }
  }, [user?.token, dispatch])

  if (Number(isMaintenance) === 1) {
    return <Maintenance />
  }

  return (
    <Router>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="company-selection" element={<PublicRoutes />}>
            <Route
              path="/company-selection"
              name="Company Selection Page"
              element={<CompanySelection />}
            />
          </Route>

          <Route path="login" element={<PublicRoutes />}>
            <Route path="/login" name="Login Page" element={<Login />} />
          </Route>

          <Route path="register" element={<PublicRoutes />}>
            <Route
              path="/register"
              name="Register Page"
              element={<Register />}
            />
          </Route>

          <Route
            exact
            path="/resetpassword"
            name="Reset password"
            element={<Resetpasswod />}
          />

          <Route
            exact
            path="/forgotPassword"
            name="Forget password"
            element={<ForgetPassword />}
          />

          <Route
            exact
            path="/otpLogin"
            name="OTP Page"
            element={<LoginOTP />}
          />
          <Route
            exact
            path="/ADRConsumer"
            name="ADR Consumer"
            element={<FormADR />}
          />

          <Route
            exact
            path="/changepassword"
            name="Change password"
            element={<ChangePassword />}
          />

          <Route
            exact
            path="/ResetpasswodLink"
            name="Reset passwod Link"
            element={<ResetpasswodLinlk />}
          />
          <Route
            exact
            path="/formADRRF"
            name="ADRRF voluntary"
            element={<FormADRRF />}
          />

          <Route exact path="/formTGA" name="TGA" element={<TGA />} />

          <Route path="/" element={<ProtectedRoutes />}>
            <Route path="*" name="Home" element={<DefaultLayout />} />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  )
}

export default App
